/**
 * The turnaround, in A-CDM milestones.
 *
 * An arrival and a departure are separate movements. A turnaround is the
 * ground time between them, and A-CDM is the agreement about who owns which
 * part of it. The milestones are not decoration: each one has an owner, and
 * the whole method rests on the difference between a prediction and a
 * commitment.
 *
 *   ALDT  actual landing time            the air side's fact
 *   AIBT  actual in-block time           on stand, chocks on
 *   TOBT  target off-block time          THE HANDLER'S COMMITMENT
 *   TSAT  target start-up approval time  ATC's answer to that commitment
 *   AOBT  actual off-block time          chocks off, pushback
 *   ATOT  actual take-off time           airborne
 *
 * ── the one distinction the method is built on ──────────────────────────
 *
 * **TOBT is a commitment, not an estimate.** The handler says "this aircraft
 * will be ready to move at 08:40", ATC allocates a start-up slot against that
 * word, and the sequence downstream is built on it. A TOBT that is quietly
 * updated whenever it is about to be missed is not a commitment, it is a
 * running commentary, and an airport whose TOBTs behave that way gets no
 * slots. So this file never revises a TOBT. It reports three things about it:
 * whether it was achievable when it was made, whether it has expired, and
 * whether it was met.
 *
 * ── what is checkable, and what is not ──────────────────────────────────
 *
 * A minimum turnaround time is a real constraint — an aircraft of a given
 * size carrying passengers cannot be emptied, cleaned, fuelled, loaded and
 * closed in less than a known number of minutes. So "AIBT + minimum > TOBT"
 * is a statement that can be CHECKED, and it is the most useful thing this
 * file computes: it says a commitment cannot be met at the moment it is
 * made, rather than at the moment it is missed.
 *
 * The minimum times below are PROPOSED. They are the figures a handling
 * agreement usually states and no operator here has agreed them.
 */
import { t, tn } from "../i18n";
import { bestKnown, delayMinutes, flightName, type Flight, type SizeCode } from "./flights";
import type { StandRegister } from "./stands";

export interface Turnaround {
  ref: string;
  arrival?: Flight;
  departure?: Flight;
  /** the stand it is parked on, where one is assigned */
  standCode?: string;
  /** on stand — from the stand assignment, or the arrival's actual time */
  aibt?: string;
  /** the handler's commitment */
  tobt?: string;
  /** ATC's start-up approval against that commitment */
  tsat?: string;
  /** chocks off */
  aobt?: string;
}

/**
 * The shortest ground time a turnaround of this kind can take. PROPOSED.
 *
 * By aircraft size and by what the aircraft is doing: a cargo turnaround has
 * no passengers to board and a positioning flight has neither passengers nor
 * a full clean, so the same airframe needs different times for different
 * work. A figure that varied only by size would be wrong for two of the three.
 */
export const MINIMUM_TURNAROUND: Record<SizeCode, number> = { A: 20, B: 25, C: 35, D: 50, E: 65, F: 90 };

export function minimumTurnaround(size: SizeCode | undefined, service: Flight["serviceType"]): number | null {
  if (!size) return null;
  const base = MINIMUM_TURNAROUND[size];
  if (service === "cargo") return Math.round(base * 1.2);
  if (service === "ferry" || service === "positioning") return Math.round(base * 0.5);
  return base;
}

const ms = (iso: string | undefined): number | null => {
  if (!iso) return null;
  const n = Date.parse(iso);
  return Number.isNaN(n) ? null : n;
};

/**
 * Build the turnarounds from the movements and the stand plan.
 *
 * A movement with no turnaround reference belongs to no turnaround — it is
 * not paired up by guessing from the registration or the times, because a
 * guessed pairing produces a ground time nobody can account for and the
 * airport is billed on ground time.
 */
export function turnaroundsOf(flights: readonly Flight[], register?: StandRegister): Turnaround[] {
  const byRef = new Map<string, Turnaround>();
  for (const f of flights) {
    if (!f.turnaround || f.source === "forecast") continue;
    const entry = byRef.get(f.turnaround) ?? { ref: f.turnaround };
    if (f.direction === "arrival") entry.arrival = f;
    else entry.departure = f;
    byRef.set(f.turnaround, entry);
  }
  for (const entry of byRef.values()) {
    const assignment = register?.assignments.find(
      (a) => a.flightId === entry.arrival?.id || a.flightId === entry.departure?.id,
    );
    entry.standCode = assignment?.standCode;
    /* on stand: the assignment's own start where it is an actual, else the landing */
    entry.aibt = assignment?.actual ? assignment.from : entry.arrival?.actualAt;
    entry.aobt = entry.departure?.actualAt;
    /* the commitment is the departure's, as the handler gave it; never a time made up here */
    if (entry.departure?.tobt) entry.tobt = entry.departure.tobt;
  }
  return [...byRef.values()].sort((a, b) =>
    (bestKnown(a.arrival ?? a.departure ?? ({} as Flight)).at ?? "").localeCompare(
      bestKnown(b.arrival ?? b.departure ?? ({} as Flight)).at ?? "",
    ),
  );
}

/**
 * The handler's commitment, where the sample does not carry one.
 *
 * Derived from the departure's schedule rather than invented: a handler
 * commits to being ready for the scheduled off-block time unless they say
 * otherwise. Marked as derived so a screen can say so — a TOBT nobody
 * actually committed to is not a commitment, and showing it as one would be
 * the exact failure this file is written against.
 */
export function derivedTobt(turn: Turnaround): { at: string | null; derived: boolean } {
  if (turn.tobt) return { at: turn.tobt, derived: false };
  const sched = turn.departure?.scheduledAt;
  return sched ? { at: sched, derived: true } : { at: null, derived: false };
}

export type TurnaroundState =
  | "not_arrived"
  | "on_stand"
  | "at_risk"
  | "commitment_missed"
  | "moving"
  | "complete";

export interface TurnaroundCheck {
  state: TurnaroundState;
  /** minutes of ground time, planned or actual */
  groundMinutes: number | null;
  minimum: number | null;
  /** the commitment cannot be met from the moment it was made */
  unachievable: boolean;
  /** the commitment is in the past and the aircraft has not moved */
  expired: boolean;
  /** minutes the commitment was missed by, once the aircraft moved */
  missedBy: number | null;
  /** what it does to the departure's own schedule */
  departureDelay: number | null;
  /** said plainly, in one line */
  says: string;
}

/**
 * What is true about this turnaround, and what is going to be.
 *
 * Everything here is checkable from the record. Nothing is predicted: the
 * closest this comes is saying a commitment cannot be met, which is a
 * comparison of two recorded numbers and a published minimum.
 */
export function checkTurnaround(turn: Turnaround, now: Date): TurnaroundCheck {
  const tobt = derivedTobt(turn);
  const aibt = ms(turn.aibt);
  const aobt = ms(turn.aobt);
  const tobtMs = ms(tobt.at ?? undefined);
  const size = turn.departure?.size ?? turn.arrival?.size;
  const minimum = minimumTurnaround(size, turn.departure?.serviceType ?? turn.arrival?.serviceType);

  const groundMinutes =
    aibt !== null && aobt !== null
      ? Math.round((aobt - aibt) / 60_000)
      : aibt !== null && tobtMs !== null
        ? Math.round((tobtMs - aibt) / 60_000)
        : null;

  const unachievable =
    aibt !== null && tobtMs !== null && minimum !== null && aibt + minimum * 60_000 > tobtMs && aobt === null;
  const expired = aibt !== null && aobt === null && tobtMs !== null && tobtMs < now.getTime();
  const missedBy = aobt !== null && tobtMs !== null ? Math.round((aobt - tobtMs) / 60_000) : null;
  const departureDelay = turn.departure ? delayMinutes(turn.departure) : null;

  let state: TurnaroundState;
  if (aobt !== null) state = "complete";
  else if (aibt === null) state = "not_arrived";
  else if (expired) state = "commitment_missed";
  else if (unachievable) state = "at_risk";
  else state = "on_stand";

  const name = turn.departure ? flightName(turn.departure) : turn.arrival ? flightName(turn.arrival) : turn.ref;
  const says =
    state === "not_arrived"
      ? t("{flight} has not arrived on stand yet.", { flight: name })
      : state === "complete"
        ? missedBy === null
          ? t("{flight} is away.", { flight: name })
          : missedBy > 0
            ? t("{flight} pushed back {n} after the time the handler committed to.", {
                flight: name,
                n: tn(missedBy, "{n} minute", "{n} minutes"),
              })
            : t("{flight} pushed back on the handler's own commitment.", { flight: name })
        : state === "commitment_missed"
          ? t("{flight} has not moved and the committed off-block time has passed.", { flight: name })
          : state === "at_risk"
            ? t(
                "{flight} cannot make its committed off-block time: it came on stand at {aibt} and a turnaround of this kind takes at least {min}.",
                {
                  flight: name,
                  aibt: (turn.aibt ?? "").slice(11, 16),
                  min: tn(minimum ?? 0, "{n} minute", "{n} minutes"),
                },
              )
            : t("{flight} is on stand and has time in hand.", { flight: name });

  return { state, groundMinutes, minimum, unachievable, expired, missedBy, departureDelay, says };
}

export const TURNAROUND_LABEL: Record<TurnaroundState, string> = {
  not_arrived: "Not on stand",
  on_stand: "On stand",
  at_risk: "Cannot make it",
  commitment_missed: "Commitment passed",
  moving: "Moving",
  complete: "Away",
};

/** The day's turnarounds, worst first — which is what a duty manager wants. */
export function sortByTrouble(turns: readonly { check: TurnaroundCheck }[]): number[] {
  const order: TurnaroundState[] = ["at_risk", "commitment_missed", "on_stand", "not_arrived", "moving", "complete"];
  return turns
    .map((x, i) => ({ i, rank: order.indexOf(x.check.state) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.i);
}
